import { useDispatch, useSelector } from "react-redux";
import DashboardPagesTemplate from "../../Template/DashboardPagesTemplate";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useTranslation } from "react-i18next";
import DashboardPageTitle from "../../Components/InnerPageTitle";
import WebsiteListTableCellBlock from "../../Components/WebsiteListTableCell/WebsiteActivePlan";

import "../../Assets/scss/pages/website-list.scss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDomainListWithDetailsService } from "../../Services/DomainService/DomainList";
import { websiteListPropType } from "../../PropTypes/WebsitePropTypes";
import PlatformWebsiteTableCellBlock from "../../Components/WebsiteListTableCell/PlatformWebsitePlan";
import ExpiredPlanWebsiteListTableCellBlock from "../../Components/WebsiteListTableCell/WebsiteExpiredPlan";
import ShowRecordItem from "../../Components/ShowRecordItem";
import PaginationComponent from "../../Components/Pagination";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import LodingImg from "../../Assets/img/icon-spinner.svg";
import { debounce } from "lodash";
import TrialPlanWebsiteListTableCellBlock from "../../Components/WebsiteListTableCell/WebsiteTrialPlan";
import { SET_MESSAGE } from "../../Actions/Types";
import CopyWidgetScriptCode from "../../Components/ScriptCode";
import useWindowScroll from "../../Helpers/useWindowScroll";
import { DomainDetailsService } from "../../Services/DomainService/DomainDetails";
import { useNavigate } from "react-router-dom";
import CancelPlanWebsiteListTableCellBlock from "../../Components/WebsiteListTableCell/WebsiteCancelPlan";
import DateRangeFilterBlock from "../../Components/DateRangeFilterBlock";
import NoRecordFound from "../../Components/NoRecordFound";
import { DomainDeleteService } from "../../Services/DomainService/DomainDelete";
import { domainValidationService } from "../../Services/DomainService/DomainValidationService";
import { EditDomainService } from "../../Services/DomainService/EditDomain";

// auto login URL
const websiteAutoLoginURL = `${process.env.REACT_APP_BASE_URL}/front/autologin`


// component to display website list page
const WebsiteListPage = () => {

  // Redux Data
  const { user, accessToken, isLoading } = useSelector((store: StoreProptypes) => store);

  // Functions
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const windowScrollValue = useWindowScroll('aioa_dashboard-website-list-filter');
  // State
  const [domainList, setDomainList] = useState<websiteListPropType[]>([]);
  const [isDomainLoading, setIsDomainLoading] = useState(false);
  const [totalMainRecord, setTotalMainRecord] = useState<number>(0);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [terms, setTerms] = useState<string>("");
  const [domainFilter, setDomainFilter] = useState<number>(4);
  const [startDate, setStartDate] = useState<string>(``);
  const [endDate, setEndDate] = useState<string>(``);

  const [isAutoLoginModal, setIsAutoLoginModal] = useState<boolean>(false);
  const [isCopyLinkSuccess, setIsCopyLinkSuccess] = useState<boolean>(false);
  const [autoLoginModalToken, setAutoLoginModalToken] = useState<string>("");
  const [autoLoginModalWebsiteURL, setAutoLoginModalWebsiteURL] = useState<string>("");

  const [isWidgetScriptCodeModal, setIsWidgetScriptCodeModal] = useState<boolean>(false);
  const [widgetScriptCodeModalPosition, setWidgetScriptCodeModalPosition] = useState<string>("");
  const [widgetScriptCodeModalColor, setWidgetScriptCodeModalColor] = useState<string>("");
  const [widgetScriptCodeModalAPIKey, setWidgetScriptCodeModalAPIKey] = useState<string>("");
  const [widgetScriptCodeModalWebsiteURL, setWidgetScriptCodeModalWebsiteURL] = useState<string>("");
  const [isMultipleDomain, setIsMultipleDomain] = useState<boolean>(false)
  const [isDeleteDomainConfirmationPopup, setIsDeleteDomainConfirmationPopup] = useState<boolean>(false)
  const [deleteDomainId, setDeleteDomainId] = useState<number>(0)

  const [isEditDomainNameModal, setIsEditDomainNameModal] = useState<boolean>(false);
  const [editWebsiteId, setEditWebsiteId] = useState<number>(0);
  const [editDomainName, setEditDomainName] = useState<string>("");
  const [isEditDomainConfirmed, setIsEditDomainConfirmed] = useState<boolean>(false)

  const prevLimitRef = useRef<number>(10);
  const prevDomainFilterRef = useRef<number>(4);



  // Pagination Functions
  useEffect(() => {
    setCurrentPage(1);
  }, [limit, terms, domainFilter]);

  useEffect(() => {
    setOffset(0);
  }, [terms, domainFilter]);

  useEffect(() => {
    setOffset(currentPage * limit - limit);
  }, [currentPage, limit]);

  // function to handle website search
  const searchChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTerms(e.target.value);
  };

  // function to throttle search handler call on user input
  const debouncedSearchChangeHandler = useMemo(() => {
    return debounce(searchChangeHandler, 500);
  }, []);


  useEffect(() => {
    // check if access token is available, limit and domain search is not changed
    if (accessToken && ((limit === prevLimitRef.current) || (domainFilter === prevDomainFilterRef.current))) {
      // service to get domain list with filters
      getDomainListWithDetailsService(accessToken, dispatch, t, setDomainList, limit, offset, terms, domainFilter, startDate, endDate, setTotalMainRecord, setTotalRecord, setIsDomainLoading);
    }
  }, [offset, terms, user, startDate, endDate]);

  useEffect(() => {
    // check if limit is changed, or domain search is changed; and offset is set to 0
    if ((limit !== prevLimitRef.current) || (domainFilter !== prevDomainFilterRef.current) && offset === 0) {
      // check if access token exists
      if (accessToken) {
        // service to get domain list with filters
        getDomainListWithDetailsService(accessToken, dispatch, t, setDomainList, limit, offset, terms, domainFilter, startDate, endDate, setTotalMainRecord, setTotalRecord, setIsDomainLoading);
      }

      prevLimitRef.current = limit;
      prevDomainFilterRef.current = domainFilter;
    }
  }, [offset, limit, domainFilter]);

  useEffect(() => {
    if (user) {
      setIsMultipleDomain(user.is_multiple_domain === 2 ? true : false)
    }
  },[user])

  // Auto Login Model Function
  const autoLinkRef = useRef(null);

  // function to display auto login modal
  const handleShowAutoLoginModal = (token: string, websiteURL: string) => {
    setAutoLoginModalToken(`${websiteAutoLoginURL}/${token}`);
    setAutoLoginModalWebsiteURL(websiteURL);
    setIsAutoLoginModal(true);
  }
  // function to hide auto login modal
  const handleHideAutoLoginModal = () => {
    setIsAutoLoginModal(false);
    setAutoLoginModalToken('');
    setAutoLoginModalWebsiteURL('');
  }
  // function to handle copy of auto login code
  const handleAutoLoginCopyCode = () => {
    navigator.clipboard.writeText(autoLoginModalToken);
    setIsCopyLinkSuccess(true);
    dispatch({
      type: SET_MESSAGE,
      payload: {
        "toast_message": t('Copied'),
        "toast_type": "success"
      }
    })
    setTimeout(() => {
      setIsCopyLinkSuccess(false);
      handleHideAutoLoginModal();
    }, 1000)
  }

  // function to display widget script modal
  const handleShowWidgetScriptCodeModal = (widgetPosition: string, widgetColor: string, widgetAPIKey: string, websiteURL: string) => {
    setWidgetScriptCodeModalPosition(widgetPosition);
    setWidgetScriptCodeModalColor(widgetColor);
    setWidgetScriptCodeModalAPIKey(widgetAPIKey);
    setWidgetScriptCodeModalWebsiteURL(websiteURL);
    setIsWidgetScriptCodeModal(true);
  }
  // function to hide widget script modal
  const handleHideWidgetScriptCodeModal = () => {
    setWidgetScriptCodeModalPosition('');
    setWidgetScriptCodeModalColor('');
    setWidgetScriptCodeModalAPIKey('');
    setWidgetScriptCodeModalWebsiteURL('');
    setIsWidgetScriptCodeModal(false);
  }

  // function to set current website
  const handleSetCurrentWebsite = (websiteID: number) => {
    // check if access token and website ID are available
    if (accessToken && websiteID) {

      // service to get domain details
      DomainDetailsService(accessToken, websiteID, dispatch);

      // navigate to dashboard page
      navigate('/front');
    }
  }
  const handleDeleteDomain = (id:number) => {
    setIsDeleteDomainConfirmationPopup(true)
    setDeleteDomainId(id)

  }

  const handleHideDeleteConfirmationPopup = () => {
    setIsDeleteDomainConfirmationPopup(false)
    setDeleteDomainId(0)
  }

  const handleDeleteDomainAction = () => {
    if (accessToken) {
      DomainDeleteService(accessToken, deleteDomainId, dispatch, domainList.filter(d => d.id !== deleteDomainId)[0].id, domainList.find(d => d.id === deleteDomainId)?.domain || '')
    }
    setIsDeleteDomainConfirmationPopup(false)
  }
  const handleISEditModelName = (websiteID:number) => {
    setEditWebsiteId(websiteID)
    setEditDomainName(domainList.find(d => d.id === websiteID)?.domain || '')
    setIsEditDomainNameModal(true)
  }

  const handleHideEditDomainNameModel = () => {
    setEditWebsiteId(0)
    setEditDomainName("")
    setIsEditDomainNameModal(false)
  }

  const editDomain = () => {
    console.log("webiste", editWebsiteId);
    domainValidationService((editDomainName.replaceAll(" ", "").replace(/\s+/g, '')), dispatch, t).then((res) => {
        // check if response is ok
        if (res.states === 200) {
            setIsEditDomainNameModal(false)
            setIsEditDomainConfirmed(true)
        }
    })
  }

  const confirmEditDomain = () => {
    if (accessToken) {
      EditDomainService(accessToken, editWebsiteId, (editDomainName.replaceAll(" ", "").replace(/\s+/g, '')), dispatch, domainList.find(d => d.id === editWebsiteId)?.domain || '').then((res) => {
          // check if response is ok
          if (res.status === 200) {
            setIsEditDomainConfirmed(false)
          }
      })
    }
  }

   // function to hide widget script modal
  const handleHideWidgetScriptConfirmModal = () => {
    setEditWebsiteId(0)
    setEditDomainName("")
    setIsEditDomainNameModal(false)
    setIsEditDomainConfirmed(false);
  }

  return (
    <>
      <DashboardPagesTemplate>
        <DashboardPageTitle
          pageTitle={`${t("Websites")}`}
          subPageTitle={`${t(`Total Domain`, { domainCount: totalMainRecord })}`}
        />

        <div className="aioa_dashboard-website-list">
          <div className={`aioa_dashboard-website-list-filter-wrapper ${windowScrollValue}`} id="aioa_dashboard-website-list-filter">

            {user && user.department === 1 && (
              <>
                <div className="aioa_dashboard-website-list-filter-block">
                  <DateRangeFilterBlock
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                  />
                </div>
              </>
            )}

            <div className="aioa_dashboard-website-list-filter-block">
              <Form.Label className='d-lg-none'>{t("Search")}</Form.Label>
              <InputGroup>
                <Form.Control
                  placeholder={t("Search Domain").toString()}
                  aria-label={t("Search Domain").toString()}
                  type="text"
                  onChange={debouncedSearchChangeHandler}
                />
                <InputGroup.Text><i className="material-symbols-outlined" aria-hidden="true">search</i></InputGroup.Text>
              </InputGroup>
            </div>

            <div className="aioa_dashboard-website-list-filter-block">
              <Form.Label htmlFor='domain_status'>{t("Filter")}</Form.Label>
              <Form.Select
                onChange={(e) => setDomainFilter(parseInt(e.target.value))}
                id="domain_status"
                aria-label={t("Filter").toString()}
                defaultValue={domainFilter ? domainFilter : 4}
              >
                <option value="4">{t("All")}</option>
                <option value="10">{t("Active")}</option>
                <option value="1">{t("Pending Activation")}</option>
                <option value="2">{t("trials")}</option>
                <option value="3">{t("Expired")}</option>
                <option value="5">{t("Canceled")}</option>
              </Form.Select>
            </div>

            <div className="aioa_dashboard-website-list-filter-block">
              <Form.Label htmlFor='showPages'>{t("Items Per Page")}</Form.Label>
              <Form.Select
                onChange={(e) => setLimit(parseInt(e.target.value))}
                id="showPages"
                value={limit}
                aria-label={t("Items Per Page").toString()}
              >
                <option value="10">{t("10")}</option>
                <option value="20">{t("20")}</option>
                <option value="40">{t("40")}</option>
                <option value="80">{t("80")}</option>
                <option value="100">{t("100")}</option>
              </Form.Select>
            </div>

          </div>

          <div className="aioa_dashboard-table">

            <div className="aioa_dashboard-table-thead">
              <div className="aioa_dashboard-table-tr">
                <div className="aioa_dashboard-table-th tcw-100 aioa_dashboard-table-cell-sidegap">{t("Sr No")}</div>
                <div className="aioa_dashboard-table-th tcw-350 aioa_dashboard-table-cell-sidegap">{t("Domain")}</div>
                <div className="aioa_dashboard-table-th tcw-150 text-center aioa_dashboard-table-cell-sidegap">{t("Plan")}</div>
                <div className="aioa_dashboard-table-th tcw-150 text-center aioa_dashboard-table-cell-sidegap">{t("Status")}</div>
                <div className="aioa_dashboard-table-th tcw-150 text-center aioa_dashboard-table-cell-sidegap">{t("Price(USD)")}</div>
                <div className="aioa_dashboard-table-th tcw-150 text-center aioa_dashboard-table-cell-sidegap">{t("Date")}</div>
                {user && user.department === 1 ? (
                  <>
                    <div className="aioa_dashboard-table-th tcw-150 text-center aioa_dashboard-table-cell-sidegap">{t("Total/Scan URLs")}</div>
                    <div className="aioa_dashboard-table-th tcw-150 text-center aioa_dashboard-table-cell-sidegap">{t("Used Ai Credits")}</div>
                  </>
                ) : (
                  <>
                    {user && (
                      <>
                       {(user?.parent_id === 0 || user?.parent_id === 1) && (<div className="aioa_dashboard-table-th tcw-250 text-center aioa_dashboard-table-cell-sidegap">{t("Manage Plan")}</div>)} 
                      </>
                    )}
                  </>
                )}
                  {(user?.parent_id === 0 || user?.parent_id === 1) &&  (<div className="aioa_dashboard-table-th tcw-50 text-center aioa_dashboard-table-cell-sidegap">{t("Action")}</div>)}
              </div>
            </div>

            <div className="aioa_dashboard-table-body" aria-live='polite'>
              {isDomainLoading ? (
      
                        <div className="aioa_dashboard-loader d-flex justify-content-center m-0">
                            <img src={LodingImg} width="50px" alt="" />
                            <span className="visually-hidden">{t("Loading page. Please Do not refresh the page")} </span>
                        </div>
                ) : domainList.length > 0 ? (
                    <>
                        {domainList.map((domain, index) => {
                                        return (
                                          <React.Fragment key={domain.id}>
                                            {(!domain.is_from_dashboard && (domain.platform === "wix" || domain.platform === 'shopify' || domain.platform === "Opencart-Pro" || domain.platform === "Magento-Pro" || domain.platform === 'AWS')) ? (
                                              <>
                                                <PlatformWebsiteTableCellBlock item={domain} key={domain.id} srno={index + 1 + offset} handleShowAutoLoginModal={handleShowAutoLoginModal} handleShowWidgetScriptCodeModal={handleShowWidgetScriptCodeModal} handleSetCurrentWebsite={handleSetCurrentWebsite} handleEditModelName={handleISEditModelName}/>
                                              </>
                                            ) : (domain.current_package_only && domain.current_package_only.length > 0) ? (
                                              <>
                                                {domain.current_package_only[0].is_trial_period === 1 ? (
                                                  <>
                                                    <TrialPlanWebsiteListTableCellBlock item={domain} key={domain.id} srno={index + 1 + offset} handleShowAutoLoginModal={handleShowAutoLoginModal} handleShowWidgetScriptCodeModal={handleShowWidgetScriptCodeModal} handleSetCurrentWebsite={handleSetCurrentWebsite} handleEditModelName={handleISEditModelName} handleDeleteDomain={handleDeleteDomain}/>
                                                  </>
                                                ) : (
                                                  <>

                                                    <WebsiteListTableCellBlock item={domain} key={domain.id} srno={index + 1 + offset} handleShowAutoLoginModal={handleShowAutoLoginModal} handleShowWidgetScriptCodeModal={handleShowWidgetScriptCodeModal} handleSetCurrentWebsite={handleSetCurrentWebsite} handleDeleteDomain={handleDeleteDomain} handleEditModelName={handleISEditModelName} />

                                                  </>
                                                )}
                                              </>
                                            ) : (domain.expired_package_detail && domain.expired_package_detail.length > 0) ? (
                                              <>
                                                <ExpiredPlanWebsiteListTableCellBlock item={domain} key={domain.id} srno={index + 1 + offset} handleShowAutoLoginModal={handleShowAutoLoginModal} handleSetCurrentWebsite={handleSetCurrentWebsite} handleEditModelName={handleISEditModelName}/>
                                              </>
                                            ) : (domain.current_package_only && domain.current_package_only.length === 0 && domain.canceled_package_only && domain.canceled_package_only.length > 0) ? (
                                              <>
                                                <CancelPlanWebsiteListTableCellBlock item={domain} key={domain.id} srno={index + 1 + offset} handleShowAutoLoginModal={handleShowAutoLoginModal} handleSetCurrentWebsite={handleSetCurrentWebsite} handleEditModelName={handleISEditModelName}/>
                                              </>
                                            ) : (
                                              <>

                                              </>
                                            )}

                                          </React.Fragment>
                                        )
                                      })}
                    </>
                ) : (
                    <NoRecordFound joyful={false} imageType="domain" noRecordText={`${t('Domain not found')}.`} />
                )}
            </div>

            <div className="aioa_dashboard-table-pagination-main">
              <>
                <ShowRecordItem
                  offset={offset}
                  limit={limit}
                  totalRecord={totalRecord}
                />
                <PaginationComponent
                  currentPage={currentPage}
                  totalRecords={totalRecord}
                  itemsPerPage={limit}
                  setCurrentPage={setCurrentPage}
                />
              </>
            </div>

          </div>

        </div>

        {/* Auto Login popup */}
        <Modal show={isAutoLoginModal} onHide={() => handleHideAutoLoginModal()} size='lg'>
          <Modal.Header closeButton>
            <Modal.Title>{t('Auto-Login Link')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='aioa_dashboard-copy-widget-code aioa_dashboard-widget-code-modal'>
              <div className='mb-3'><strong>{t('Website')}</strong> : {autoLoginModalWebsiteURL}</div>
              <div className='aioa_dashboard-widget-code '>
                <div className='aioa_dashboard-widget-code-copy-btn'>
                  <Button aria-label={t("Copy auto login link").toString()} className='mb-0' variant={`${isCopyLinkSuccess ? 'success' : 'info'}`} onClick={handleAutoLoginCopyCode}><i className={`material-symbols-outlined`} aria-hidden="true">{isCopyLinkSuccess ? 'done' : 'content_copy'}</i></Button>
                </div>
                <pre ref={autoLinkRef}>{autoLoginModalToken}</pre>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* Copy widget script code popup */}
        <Modal show={isWidgetScriptCodeModal} onHide={() => handleHideWidgetScriptCodeModal()} size='lg'>
          <Modal.Header closeButton>
            <Modal.Title>{t('Widget Code Popup Title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='mb-3'><strong>{t('Website')}</strong> : {widgetScriptCodeModalWebsiteURL}</div>
            <CopyWidgetScriptCode
              widgetAPIKey={widgetScriptCodeModalAPIKey}
              widgetPosition={widgetScriptCodeModalPosition !== 'null' ? widgetScriptCodeModalPosition : `bottom_right`}
              widgetColor={widgetScriptCodeModalColor !== 'null' ? widgetScriptCodeModalColor : '#420083'}
            />
          </Modal.Body>
        </Modal>

        <Modal show={isDeleteDomainConfirmationPopup} className="aioa_dashboard-info-modal" onHide={() => handleHideDeleteConfirmationPopup()} size="lg" fullscreen="lg-down" backdrop="static" centered>
          <Modal.Header closeButton>
              <Modal.Title>
                          {t("delete domain")}
              </Modal.Title>
          </Modal.Header>
          <Modal.Body>
              <div className="aioa_dashboard-info-modal-content">
                  {t(`are you sure you want to remove`)} &nbsp;
                  <b>{domainList.find(d => d.id === deleteDomainId)?.domain}?</b>
              </div>
          </Modal.Body>
                  <Modal.Footer>
                      <Button variant="danger" size="lg" onClick={() => handleDeleteDomainAction()}>
                          {t('delete')}
                      </Button>
                      <Button variant="primary" size="lg" onClick={() => handleHideDeleteConfirmationPopup()}>
                          {t('Cancel')}
                      </Button>
                  </Modal.Footer>
          </Modal>
        {/* Edit Domain name popup */}
        <Modal show={isEditDomainNameModal} onHide={() => handleHideEditDomainNameModel()} size='lg'>
          <Modal.Header closeButton>
            <Modal.Title>{t('update domain name')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
              <Form>
                <InputGroup className="mt-3 input-group-outline">
                    <Form.Control
                        placeholder={`${t("enter domain")}`}
                        aria-label={`${t("enter domain")}`}
                        type="text"
                        size="lg"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditDomainName(e.target.value)} value={editDomainName}
                    />
                </InputGroup>
              </Form>
          </Modal.Body>
          <Modal.Footer>
              <Button variant="success" size="lg" onClick={() => editDomain()}>
                  {t('update')}
              </Button>
              <Button variant="primary" size="lg" onClick={() => handleHideEditDomainNameModel()}>
                  {t('Cancel')}
              </Button>
          </Modal.Footer>
        </Modal>

        {/* Confirm Popup for Edit Website */}
        <Modal show={isEditDomainConfirmed} onHide={() => handleHideWidgetScriptConfirmModal()} size='lg'>
          <Modal.Header closeButton>
            <Modal.Title>{t('confirm domain')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='mb-3'>
              <div className="mb-3">
                {t('changing the domain will remove all existing reports, along with any add-on packages and widget settings associated with the current domain')}<br></br>
              </div>
              <div className="mb-3">
                 {t('this action cannot be undone')}.<br></br>
              </div>
              <b>{t('do you want to proceed?')}</b>
            </div>
          </Modal.Body>
          <Modal.Footer>
              <Button variant="success" size="lg" onClick={() => confirmEditDomain()}>
                  {t('confirm')}
              </Button>
              <Button variant="primary" size="lg" onClick={() => handleHideWidgetScriptConfirmModal()}>
                  {t('Cancel')}
              </Button>
          </Modal.Footer>
        </Modal>

      </DashboardPagesTemplate>
    </>
  )
}

export default WebsiteListPage;
